import React, { useState } from 'react'
import { StyledFooterCell, StyledHeaderCell } from '../../../../styles/WorklogTableStyles'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { t } from 'i18next'
import { roundUpToNearestMinute, getDurationString } from '../../../../store/utils/worklogUtils'
import EntryTableRow from './EntryTableRow'
import { Day, WorkDay, WorkEntry } from '../../../../store/types'
import { orderBy } from 'lodash'
import { defaultPageSize, defaultRowsPerPageOptions } from '../../../../config/defaultPagination'
import CheckboxSelectAll from '../../../Checkbox/CheckboxSelectAll'
import { Order } from '../../../../store/types/components/table/order'
import DateFormat from '../../../../store/types/enums/DateFormat'
import { StyledTableSortLabel } from '../../../../styles/TableStyles'
import LocalStorageTypes from '../../../../store/types/enums/LocalStorageTypes'

type EntryTableProps = {
  worklog: WorkDay[] | undefined
  day: Day
  rowsPerPage: number
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>
  filteringProps: {
    projects: string[]
    timeFrom: number
    timeTo: number
  }
  editMode: boolean
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>
  setEditedEntry: React.Dispatch<React.SetStateAction<WorkEntry | null>>
  todaysTotal: number
  editedEntry: WorkEntry | null
  isFilterMenuOpen: boolean
  isReadOnly: boolean
  selected: WorkEntry[]
  setSelected: React.Dispatch<React.SetStateAction<WorkEntry[]>>
}

const EntryTable = ({
  worklog,
  day,
  rowsPerPage,
  setRowsPerPage,
  filteringProps,
  editMode,
  setEditMode,
  setEditedEntry,
  todaysTotal,
  editedEntry,
  isFilterMenuOpen,
  isReadOnly,
  selected,
  setSelected,
}: EntryTableProps) => {
  // ENTRIES
  const entries: WorkEntry[] | undefined = worklog?.filter(d => d.date === day.date.format(DateFormat.YearMonthDay))[0]
    ?.entries
  const [filteredTotal, setFilteredTotal] = useState<number>(0)

  // SORT
  const [order, setOrder] = React.useState<Order>('asc')
  const [orderProperty, setOrderProperty] = React.useState<string>('name')

  const handleRequestSort = (property: string) => {
    const isAsc = orderProperty === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderProperty(property)
  }

  // PAGINATION
  const [page, setPage] = useState(0)

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    let rowsPerPage = parseInt(event.target.value, 10)
    setRowsPerPage(rowsPerPage)
    localStorage.setItem(LocalStorageTypes.EntryTableRows, String(rowsPerPage))
    setPage(0)
  }

  const filteredEntries = React.useMemo(
    () =>
      entries === undefined
        ? []
        : orderBy(
            entries.filter(
              e =>
                e.hours >= filteringProps.timeFrom &&
                e.hours <= filteringProps.timeTo &&
                filteringProps.projects.includes(e.project.id)
            ),
            orderProperty,
            order
          ),
    [entries, filteringProps, order, orderProperty]
  )

  React.useEffect(() => {
    if (filteredEntries) {
      const total = filteredEntries.reduce((acc, entry) => acc + entry.hours, 0)
      setFilteredTotal(total)
    }
  }, [filteredEntries])

  const visibleRows = React.useMemo(
    () => filteredEntries?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredEntries, page, rowsPerPage]
  )

  let rowCount = filteredEntries === undefined ? 0 : filteredEntries.length
  const emptyRows = Math.max(0, (1 + page) * Math.min(defaultPageSize, rowsPerPage) - rowCount)

  const handleClick = (event: React.MouseEvent<unknown>, workEntry: WorkEntry) => {
    const indexes = selected.map(entry => entry.id)
    const selectedIndex = indexes.indexOf(workEntry.id)
    let newSelected: WorkEntry[] = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, workEntry)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1))
    }

    setSelected(newSelected)
  }

  const isSelected = (workEntry: WorkEntry) => {
    let indexes = selected.map(entry => entry.id)
    return indexes.indexOf(workEntry.id) !== -1
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <TableContainer
        sx={{
          width: '100%',
        }}
      >
        <Table sx={{ width: '100%', height: '100%', tableLayout: 'fixed' }} size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ height: '40px' }}>
              <StyledHeaderCell
                component="td"
                sx={{
                  width: isReadOnly ? '0px' : '42px',
                  minWidth: isReadOnly ? 0 : '42px',
                  padding: 0,
                  overflow: 'hidden',
                }}
              >
                {!isReadOnly && <CheckboxSelectAll data={visibleRows} selected={selected} setSelected={setSelected} />}
              </StyledHeaderCell>

              <StyledHeaderCell
                sx={{
                  width: isReadOnly ? '120px' : '90px',
                  minWidth: isReadOnly ? '120px' : '90px',
                  pl: isReadOnly ? undefined : 0,
                }}
                sortDirection={orderProperty === 'name' ? order : false}
              >
                <StyledTableSortLabel
                  active={orderProperty === 'project.name'}
                  direction={orderProperty === 'project.name' ? order : 'asc'}
                  onClick={() => handleRequestSort('project.name')}
                >
                  {t('page.worklog.table.project')}
                  {orderProperty === 'project.name' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </StyledHeaderCell>

              <StyledHeaderCell
                sx={{
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }}
              >
                {t('page.worklog.table.description')}
              </StyledHeaderCell>

              <StyledHeaderCell
                sx={{ width: '110px' }}
                align="center"
                sortDirection={orderProperty === 'hours' ? order : false}
              >
                <StyledTableSortLabel
                  active={orderProperty === 'hours'}
                  direction={orderProperty === 'hours' ? order : 'asc'}
                  onClick={() => handleRequestSort('hours')}
                  sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
                >
                  {t('page.worklog.table.timeSpent')}
                  {orderProperty === 'hours' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </StyledHeaderCell>
              <StyledHeaderCell component="td" sx={{ width: '30px', padding: 0 }}></StyledHeaderCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {visibleRows?.length === 0 ? (
              <TableRow
                style={{
                  height: 40 * Math.min(defaultPageSize, rowsPerPage),
                }}
              >
                <TableCell
                  colSpan={5}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ fontStyle: 'italic', textAlign: 'center' }}>
                    {t('page.worklog.table.entriesNotFound')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {visibleRows?.map(entry => (
                  <EntryTableRow
                    key={entry.id}
                    entry={entry}
                    editMode={editMode}
                    setEditMode={setEditMode}
                    setEditedEntry={setEditedEntry}
                    editedEntry={editedEntry}
                    isReadOnly={isReadOnly}
                    handleClick={handleClick}
                    isSelected={isSelected}
                  />
                ))}

                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: 40 * emptyRows,
                    }}
                  >
                    <TableCell colSpan={5} />
                  </TableRow>
                )}
              </>
            )}
          </TableBody>

          <TableFooter
            sx={{
              height: '40px',
              bgcolor: 'white',
              width: '100%',
              left: 0,
              bottom: 0,
              zIndex: 2,
              position: 'sticky',
            }}
          >
            <TableRow>
              <StyledFooterCell sx={{ width: '20px' }}></StyledFooterCell>
              <StyledFooterCell colSpan={2}>
                <Typography variant="body2" fontWeight={700}>
                  {t('page.worklog.table.sum')}
                </Typography>
              </StyledFooterCell>
              <StyledFooterCell align="center">
                <Typography variant="body2" fontWeight={700}>
                  {getDurationString(roundUpToNearestMinute(filteredTotal))}
                </Typography>
              </StyledFooterCell>
              <StyledFooterCell sx={{ width: '50px' }}></StyledFooterCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        <TablePagination
          component="div"
          rowsPerPageOptions={defaultRowsPerPageOptions}
          count={rowCount}
          rowsPerPage={rowsPerPage}
          page={page}
          labelRowsPerPage={t('components.table.pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => {
            if (from > count) {
              setPage(page - 1)
            }
            return `${from} - ${to} ${t('components.table.pagination.of')} ${count}`
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            color: 'text.highEmphasis',
            '& .MuiToolbar-root': { padding: 0 },
          }}
        />
      </Box>
    </Box>
  )
}

export default EntryTable
